import {
  type Quote,
  completePurchase,
  confirmQuote,
  failPurchase,
  reportEthSent,
  startPurchase,
} from '@/services/api'
import { useWallet } from '@components/providers/WalletProvider'
import { useSmartWallets } from '@privy-io/react-auth/smart-wallets'
import { fetchAuthSession } from 'aws-amplify/auth'
import { useCallback, useState } from 'react'
import { type Address, parseEther } from 'viem'

export type PurchaseStatus =
  | 'idle'
  | 'confirming' // 見積もり確定中
  | 'awaiting_signature' // ユーザー署名待ち
  | 'sending_eth' // ETH送金中
  | 'completed' // 購入手続き完了（ARXはバックグラウンドで送金される）
  | 'error' // エラー

export interface UsePurchaseOrderReturn {
  // 購入ステータス
  status: PurchaseStatus
  // エラーメッセージ
  error: string | null
  // ETHトランザクションハッシュ
  txHash: string | null
  // 購入を実行する関数（arxAmountはユーザーの入力量、feeWeiはガス代見積もり）
  executePurchase: (quote: Quote, arxAmount: string, feeWei: bigint) => Promise<boolean>
  // ステータスをリセットする関数
  reset: () => void
}

export function toUserFriendlyMessage(raw: string): string {
  const lower = raw.toLowerCase()
  // ウォレットSDK層: ユーザーがトランザクション署名をキャンセルした場合
  if (lower.includes('user rejected') || lower.includes('user denied')) {
    return 'ウォレットで購入がキャンセルされました。'
  }
  // ウォレットSDK層: ガス代を含むETH残高が不足している場合
  if (lower.includes('insufficient funds')) {
    return '残高が不足しています。残高を確認してから再度お試しください。'
  }
  // cognito-auth.ts: "Authentication required" (401), "JWT verification failed" (401)
  if (raw.includes('Authentication required') || raw.includes('JWT verification failed')) {
    return '認証に失敗しました。再ログインしてからお試しください。'
  }
  // quote-service.ts → safe-error.ts SAFE_ERROR_PATTERNS: "Quote has expired"
  if (raw.includes('Quote has expired')) {
    return '見積もりの有効期限が切れました。画面を更新して再度お試しください。'
  }
  // usePurchaseOrder.ts ローカルフォールバック
  if (raw.includes('ETH送金の検証に失敗しました')) {
    return raw
  }
  return '購入処理中にエラーが発生しました。しばらく時間をおいてから再度お試しください。'
}

export function usePurchaseOrder(): UsePurchaseOrderReturn {
  const { smartWalletAddress, isSmartWalletReady } = useWallet()
  const { client } = useSmartWallets()

  const [status, setStatus] = useState<PurchaseStatus>('idle')
  const [error, setError] = useState<string | null>(null)
  const [txHash, setTxHash] = useState<string | null>(null)

  const executePurchase = useCallback(
    async (quote: Quote, arxAmount: string, feeWei: bigint): Promise<boolean> => {
      if (!smartWalletAddress || !client || !isSmartWalletReady) {
        setError('ウォレットが準備されていません')
        setStatus('error')
        return false
      }

      setStatus('confirming')
      setError(null)
      setTxHash(null)

      let historyId: string | null = null

      try {
        console.log(
          '[usePurchaseOrder] Starting purchase with smartWalletAddress:',
          smartWalletAddress
        )

        // 1. 見積もりを確定してOTCウォレットアドレスを取得
        // 表示用Quoteのレートを使って、実際の購入量で再計算する
        const confirmResponse = await confirmQuote(quote.quoteId, quote.hmac, arxAmount)

        if (!confirmResponse.success) {
          throw new Error(confirmResponse.error || '見積もりの確定に失敗しました')
        }

        // 注: ethAmountはサーバー側でarxAmountに基づいて再計算される
        const { otcWalletAddress, ethAmount } = confirmResponse.order

        if (!otcWalletAddress) {
          throw new Error('OTCウォレットアドレスが取得できませんでした')
        }

        // 2. 購入履歴を作成（PENDING状態、Orderにリンク）
        try {
          const startResult = await startPurchase(arxAmount, quote.quoteId)
          if (startResult.success && startResult.historyId) {
            historyId = startResult.historyId
            console.log('Purchase history created (PENDING):', historyId)
          } else {
            console.warn('Failed to create purchase history:', startResult.error)
          }
        } catch (startError) {
          console.warn('Failed to create purchase history:', startError)
        }

        // 3. ETHを送金
        setStatus('awaiting_signature')

        const ethValue = parseEther(ethAmount)
        const paymasterAddress = import.meta.env.VITE_PAYMASTER_ADDRESS as Address

        setStatus('sending_eth')

        const session = await fetchAuthSession()
        const authToken = session.tokens?.idToken?.toString()

        console.log('[usePurchaseOrder] Sending ETH transaction:')
        console.log('  - from (smartWalletAddress):', smartWalletAddress)
        console.log('  - to (otcWalletAddress):', otcWalletAddress)
        console.log('  - value:', ethAmount, 'ETH')
        console.log('  - feeWei:', feeWei.toString())

        const hash = await client.sendTransaction(
          {
            calls: [
              { to: paymasterAddress, value: feeWei },
              { to: otcWalletAddress as Address, value: ethValue },
            ],
            paymasterContext: { payWith: 'eth', authToken },
          },
          { uiOptions: { showWalletUIs: false } }
        )

        console.log('ETH transaction sent:', hash)
        setTxHash(hash)

        // 4. ETH送金をサーバーに報告（検証必須）
        const reportResult = await reportEthSent(quote.quoteId, hash)

        // pendingRecovery: トランザクション検証がタイムアウトしたが、自動復旧される
        // この場合は成功として扱う（バックグラウンドで自動的に処理が完了する）
        if (reportResult.pendingRecovery) {
          console.log('Transaction verification timed out, but will be auto-recovered')
          console.log('Message:', reportResult.message)
          // 自動復旧されるため、成功として扱う - エラーをスローしない
        } else if (!reportResult.success) {
          // ETH送金は完了しているが、サーバー検証が完全に失敗（復旧不可）
          // ユーザーに明確なエラーメッセージを表示
          throw new Error(
            reportResult.error ||
              'ETH送金の検証に失敗しました。トランザクションを確認してサポートにお問い合わせください。'
          )
        } else {
          console.log('ETH sent reported to server')
        }

        // 5. 購入履歴をCOMPLETEDに更新
        // 注: ARXはバックグラウンドで送金されるが、ユーザーの購入アクションは完了
        if (historyId) {
          try {
            const completeResult = await completePurchase(historyId, hash)
            if (completeResult.success) {
              console.log('Purchase history completed:', completeResult.history)
            } else {
              console.warn('Failed to complete purchase history:', completeResult.error)
            }
          } catch (completeError) {
            console.warn('Failed to complete purchase history:', completeError)
          }
        }

        // 6. 購入手続き完了
        // ARXはOTCデスクのバックグラウンド処理で数分以内に送金される
        setStatus('completed')
        return true
      } catch (err) {
        console.error('Purchase error:', err)
        const raw = err instanceof Error ? err.message : String(err)
        const errorMessage = toUserFriendlyMessage(raw)
        setError(errorMessage)
        setStatus('error')

        // 履歴をFAILEDに更新
        if (historyId) {
          try {
            await failPurchase(historyId, errorMessage)
            console.log('Purchase history marked as failed:', historyId)
          } catch (failError) {
            console.warn('Failed to mark purchase history as failed:', failError)
          }
        }

        return false
      }
    },
    [smartWalletAddress, client, isSmartWalletReady]
  )

  const reset = useCallback(() => {
    setStatus('idle')
    setError(null)
    setTxHash(null)
  }, [])

  return {
    status,
    error,
    txHash,
    executePurchase,
    reset,
  }
}
